import zlFetch from "zl-fetch"

const methods = [
  "get",
  "post",
  "put",
  "patch",
  "delete",
] as const

interface ApiOptions {
  headers?: HeadersInit
  body?: object | string | undefined | FormData
  auth?: string
  debug?: boolean
  returnError?: boolean
  customResponseParser?: boolean
  query?: object
  authenticated?: boolean
  isMerchantGroup?: boolean
}

export type ApiResponse<T> = {
  headers: Headers
  body: T
  status: number
  statusText: string
  response: Response
}

type RequestDataSource =
  | "path"
  | "query"
  | "header"
  | "cookie"
  | "body"
  | "form"
  | "file"

type RequestSourceErrorMap = {
  [location: string]: string
}

type NinjaErrorBody = {
  detail: string
  code?: string
  requestErrors?: Partial<
    Record<RequestDataSource, RequestSourceErrorMap>
  >
  otherErrors?: Array<string>
}

export class ApiError extends Error {
  status: number
  detail?: string
  code?: string
  requestErrors?: Partial<
    Record<RequestDataSource, RequestSourceErrorMap>
  >
  otherErrors?: Array<string>

  constructor(response: ApiResponse<unknown>) {
    super()

    this.status = response.status

    if (isNinjaErrorBody(response.body)) {
      this.detail = response.body.detail
      this.code = response.body.code
      this.requestErrors = response.body.requestErrors
      this.otherErrors = response.body.otherErrors
    }
  }
}

type ApiMethod = <T>(
  url: string,
  options?: ApiOptions
) => Promise<ApiResponse<T>>

type Api = Record<(typeof methods)[number], ApiMethod>

const buildUrl = (path: string) => `${__API_ROOT__}${path}`
export const api = {} as Api

for (const method of methods) {
  api[method] = async (
    path: string,
    {
      query,
      authenticated = true,
      ...options
    }: ApiOptions = {}
  ) => {
    const { accessToken, setToken } =
      options.isMerchantGroup
        ? useMerchantGroupAuth()
        : useAuth()

    const url = query
      ? buildUrl(`${path}${buildQueryString(query)}`)
      : buildUrl(path)

    try {
      return await zlFetch(url, {
        ...(authenticated && { auth: accessToken.value }),
        ...options,
        method: method.toUpperCase(),
      })
    } catch (error_) {
      const error = error_ as ApiResponse<unknown>

      if (error.status === 401) {
        setToken(null)
      }
      throw new ApiError(error)
    }
  }
}

const isNinjaErrorBody = (
  body: unknown
): body is NinjaErrorBody => {
  return (
    typeof body === "object" &&
    body !== null &&
    "detail" in body
  )
}
